import { getToolIcon } from "@/components/molecules/tool-icon";
import { Skeleton } from "@/components/ui/skeleton";
import { useToolCatalog } from "@/features/tools/hooks/use-tool-catalog";
import type { AvailableTool } from "@/features/tools/types";
import { cn } from "@/lib/utils";
import { CaretRightIcon } from "@phosphor-icons/react";
import _ from "lodash";
import type { ReactNode } from "react";

interface ToolListProps {
  items: AvailableTool[];
  isLoading?: boolean;
  /** Whether the module is on, for whoever this list speaks about. */
  isOn: (item: AvailableTool) => boolean;
  /** Title of the modules that are on, given how many there are. */
  activeTitle: (count: number) => string;
  inactiveTitle: string;
  /**
   * What a section shows when it has no module. Left out, the section is not
   * drawn at all.
   */
  emptyActive?: ReactNode;
  emptyInactive?: ReactNode;
  /** The switch at the end of the row; its clicks must stay its own. */
  renderSwitch: (item: AvailableTool, on: boolean) => ReactNode;
  /** Badges beside the name. */
  renderBadges?: (item: AvailableTool, on: boolean) => ReactNode;
  /** A line under the description (who uses the module, …). */
  renderMeta?: (item: AvailableTool, on: boolean) => ReactNode;
  /** Whether the row opens something. Every row does when left out. */
  canOpen?: (item: AvailableTool, on: boolean) => boolean;
  onOpen: (item: AvailableTool) => void;
}

/**
 * The modules, one line each — icon, name, description, a chevron and the
 * switch — the ones that are on first. Shared by the agent's skills and the
 * company's tools page, which only differ by what « on » means, what the
 * switch does and what opening a line opens.
 */
export function ToolList({
  items,
  isLoading,
  isOn,
  activeTitle,
  inactiveTitle,
  emptyActive,
  emptyInactive,
  renderSwitch,
  renderBadges,
  renderMeta,
  canOpen = () => true,
  onOpen,
}: ToolListProps) {
  const catalog = useToolCatalog();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  const byLabel = (list: AvailableTool[]) =>
    _.sortBy(list, (item) => catalog.label(item.tool));
  const active = byLabel(items.filter((item) => isOn(item)));
  const inactive = byLabel(items.filter((item) => !isOn(item)));

  const section = (title: string, list: AvailableTool[], empty?: ReactNode) =>
    (list.length > 0 || empty) && (
      <div className="space-y-2">
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          {title}
        </h3>
        {list.length > 0 ? (
          <div className="divide-y rounded-lg border">
            {list.map((item) => {
              const on = isOn(item);
              return (
                <ToolRow
                  key={item.tool.id}
                  item={item}
                  on={on}
                  openable={canOpen(item, on)}
                  onOpen={() => onOpen(item)}
                  badges={renderBadges?.(item, on)}
                  meta={renderMeta?.(item, on)}
                  toggle={renderSwitch(item, on)}
                />
              );
            })}
          </div>
        ) : (
          empty
        )}
      </div>
    );

  return (
    <div className="space-y-6">
      {section(activeTitle(active.length), active, emptyActive)}
      {section(inactiveTitle, inactive, emptyInactive)}
    </div>
  );
}

function ToolRow({
  item,
  on,
  openable,
  onOpen,
  badges,
  meta,
  toggle,
}: {
  item: AvailableTool;
  on: boolean;
  openable: boolean;
  onOpen: () => void;
  badges?: ReactNode;
  meta?: ReactNode;
  toggle: ReactNode;
}) {
  const catalog = useToolCatalog();
  const Icon = getToolIcon(item.tool.name);
  const label = catalog.label(item.tool);

  return (
    <div
      className={cn(
        "flex items-center gap-3 p-3 transition-colors",
        openable && "hover:bg-muted/40 cursor-pointer",
      )}
      onClick={openable ? onOpen : undefined}
    >
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
          on ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
        )}
      >
        <Icon weight="fill" className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {openable ? (
            <button
              type="button"
              className="focus-visible:ring-ring rounded text-left text-sm font-medium outline-none focus-visible:ring-2"
              onClick={(event) => {
                event.stopPropagation();
                onOpen();
              }}
            >
              {label}
            </button>
          ) : (
            <span className="text-sm font-medium">{label}</span>
          )}
          {badges}
        </div>
        <p className="text-muted-foreground truncate text-xs">
          {catalog.description(item.tool)}
        </p>
        {meta}
      </div>
      {openable && (
        <CaretRightIcon className="text-muted-foreground hidden h-4 w-4 shrink-0 sm:block" />
      )}
      {toggle}
    </div>
  );
}
